import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Phone, Mail, AlertTriangle, BookOpen } from 'lucide-react';
import ScamIQBadge from '../components/ScamIQBadge';
import '../styles/ScamSmart.css';

const SHIELD_LOGO = 'https://customer-assets.emergentagent.com/job_express-checkout-9/artifacts/gw8x19qe_Variant%201_Shield%20Only_Vector%20copy.png';

const SOURCES = [
  {
    name: 'Scamwatch / National Anti-Scam Centre',
    desc: 'Australia\'s primary scam reporting and education service, run by the National Anti-Scam Centre at the ACCC.',
    href: 'https://www.scamwatch.gov.au',
  },
  {
    name: 'cyber.gov.au — Australian Signals Directorate',
    desc: 'Federal cybersecurity guidance for individuals, families and small business. The official source for staying secure online.',
    href: 'https://www.cyber.gov.au',
  },
  {
    name: 'ACCC',
    desc: 'Consumer protection authority responsible for fair trading and scam consumer alerts.',
    href: 'https://www.accc.gov.au',
  },
  {
    name: 'ASIC',
    desc: 'Financial regulator publishing investment-scam and impersonation alerts.',
    href: 'https://moneysmart.gov.au/check-and-report-scams',
  },
  {
    name: 'IDCARE',
    desc: 'Australia and New Zealand\'s national identity and cyber-support community service. The right call after a scam.',
    href: 'https://www.idcare.org',
  },
  {
    name: 'Services Australia',
    desc: 'Operates myGov, Medicare, Centrelink and Services Australia scam alerts.',
    href: 'https://www.servicesaustralia.gov.au/scams',
  },
];

const ScamSmartTrusted = () => (
  <div className="ss-page" data-testid="ss-trusted-page">
    <header className="ss-header">
      <div className="ss-header-inner">
        <Link to="/scamsmart/dashboard" className="ss-brand">
          <img src={SHIELD_LOGO} alt="" className="ss-brand-mark" />
          <div>
            <div className="ss-brand-text">All Secured</div>
            <div className="ss-brand-sub">Trusted Guidance</div>
          </div>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ScamIQBadge />
          <Link to="/scamsmart/dashboard" className="ss-header-cta">Dashboard</Link>
        </div>
      </div>
    </header>

    <div className="ss-test-wrap">
      <div className="ss-test-container" style={{ maxWidth: 880 }}>
        <Link to="/scamsmart/dashboard" className="ss-back"><ArrowLeft size={14} /> Dashboard</Link>

        <div className="ss-section-eyebrow">Trusted guidance</div>
        <h1 className="ss-section-title" style={{ fontSize: 28 }}>
          Why ScamSmart's scenarios feel real.
        </h1>
        <p className="ss-section-lede" style={{ fontSize: 15 }}>
          Every scenario in ScamSmart is built around publicly reported scam patterns from trusted Australian
          consumer-safety and cybersecurity organisations. Each scenario stores a <strong>source-basis</strong> tag and a
          <strong> last-reviewed date</strong>, so the library can be maintained as scam tactics evolve.
        </p>

        <div className="ss-source-list">
          {SOURCES.map((s) => (
            <div className="ss-source-card" key={s.name}>
              <h3 className="ss-source-name"><BookOpen size={13} style={{ verticalAlign: '-2px', marginRight: 6, color: '#16B1C4' }} />{s.name}</h3>
              <p className="ss-source-desc">{s.desc}</p>
              <a className="ss-source-link" href={s.href} target="_blank" rel="noopener noreferrer">Visit official site →</a>
            </div>
          ))}
        </div>

        <div className="ss-emergency" data-testid="ss-emergency-steps">
          <h3><AlertTriangle size={16} style={{ verticalAlign: '-3px', marginRight: 6, color: '#c08220' }} />If you think you've been scammed</h3>
          <ol>
            <li><strong>Call your bank immediately</strong> using the number on the back of your card. They can sometimes halt or recall a recent transfer.</li>
            <li><strong>Change passwords</strong> for any account that may have been accessed — email first, then banking, then everything reusing that password.</li>
            <li><strong>Turn on 2-factor authentication</strong> wherever it isn't already enabled.</li>
            <li><strong>Contact IDCARE</strong> on <a href="tel:1800595160" style={{ color: '#16B1C4', fontWeight: 700 }}>1800 595 160</a> — Australia and New Zealand's national identity and cyber support service.</li>
            <li><strong>Report the scam to Scamwatch</strong> at <a href="https://www.scamwatch.gov.au/report-a-scam" target="_blank" rel="noopener noreferrer" style={{ color: '#16B1C4', fontWeight: 700 }}>scamwatch.gov.au/report-a-scam</a>.</li>
            <li><strong>Tell a trusted person</strong>. Scammers count on shame and silence — talking about it breaks that pattern and protects others.</li>
          </ol>
        </div>

        <div className="ss-trusted">
          <div className="ss-trusted-title">Disclaimer</div>
          ScamSmart is an awareness and decision-training tool. It cannot detect every scam or guarantee protection.
          Scam Radar provides a risk assessment based on red flags — a low-risk result does not guarantee a message is safe.
          Always verify important requests through official or trusted channels. ScamSmart does not provide legal or
          financial advice.
        </div>

        <p style={{ textAlign: 'center', fontSize: 12, color: '#5C6B73', marginTop: 22 }}>
          <ShieldCheck size={13} style={{ verticalAlign: '-2px', marginRight: 6, color: '#16B1C4' }} />
          ScamSmart by All Secured · scenarios last reviewed 2026-02-08
        </p>
      </div>
    </div>
  </div>
);

export default ScamSmartTrusted;
